import moment from 'moment';
import specSheetImages from 'components/specSheetImages';
import { getLocalStorageInfo, setLocalStorageInfo, _toBlob } from '../../utils';
import uuid from 'uuid/v1';
import * as Promise from 'bluebird';

export const fileSystem = () => {
  const platformId = window.cordova.platformId;

  const b64toBlob = (b64Data, contentType, sliceSize) => {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      let slice = byteCharacters.slice(offset, offset + sliceSize);

      let byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      let byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  const documentsDirectory =
    platformId === 'ios'
      ? window.cordova.file.documentsDirectory
      : window.cordova.file.externalDataDirectory || window.cordova.file.dataDirectory;
  const applicationDirectory = window.cordova.file.applicationDirectory;
  const applicationStorageDirectory = window.cordova.file.applicationStorageDirectory;
  const databaseDirectory =
    window.cordova.platformId === 'ios'
      ? window.cordova.file.documentsDirectory
      : window.cordova.file.applicationStorageDirectory + 'databases/';

  window.requestFileSystem(
    window.PERSISTENT,
    100500 * 1024 * 1024,
    () => {
      console.log('Success: Persistent file system');
    },
    error => {
      console.log('Error: ');
      throw error;
    }
  );

  const mkdirs = (rootDirectory, directory) =>
    new Promise((success, fail) => {
      const _errorHandler = error => {
        console.log('Error: ', error);
        fail(error);
      };

      window.resolveLocalFileSystemURL(
        rootDirectory,
        dirEntry => {
          dirEntry.getDirectory(
            directory,
            { create: true },
            directoryEntry => {
              success(directoryEntry);
            },
            _errorHandler
          );
        },
        _errorHandler
      );
    });

  const fsGetFile = fileUrl =>
    new Promise((success, fail) => {
      const _errorHandler = error => {
        console.log('Error: ', error);
        fail(error);
      };

      window.resolveLocalFileSystemURL(fileUrl, success, _errorHandler);
    });

  const fsDirGetFile = (directory, filename) =>
    new Promise((success, fail) => {
      const _errorHandler = error => {
        console.log('Error: ', error);
        fail(error);
      };

      getDirectory(directory).then(directoryEntry => {
        directoryEntry.getFile(filename, {}, success, _errorHandler);
      });
    });

  const getImageUrl = url => url;

  const chooseFile = (fileExt, position) =>
    new Promise((success, fail) => {
      const _checkFileType = (path, fileExt) => path.match(new RegExp(`(${fileExt.join('|')})$`, 'i'));

      var _chooseFileOk = (uri, fileExt) => {
        console.log(`FileManager#chooseFile - uri: ${uri}, fileType: ${fileExt}`);

        if (!_checkFileType(uri, fileExt)) {
          fail('wrong_file_type');
        } else {
          setTimeout(
            () => (uri.indexOf('file://') >= 0 ? success(uri) : success(`file://${uri}`)),

            1000
          );
        }
      };

      const _chooseFileError = error => {
        console.log(`FileManager#chooseFile - error: ${JSON.stringify(error)}`);

        // assume operation cancelled
        fail('cancelled');
      };

      if (platformId.toLowerCase() === 'ios') {
        // iOS (NOTE - only iOS 8 and higher): https://github.com/jcesarmobile/FilePicker-Phonegap-iOS-Plugin
        const utis = ['public.data', 'public.archive'];

        FilePicker.pickFile(
          uri => {
            _chooseFileOk(uri, fileExt);
          },
          error => {
            _chooseFileError(error);
          },
          utis,
          position
        );
      } else {
        console.log('here');
        // Android: https://github.com/ihadeed/cordova-filechooser
        fileChooser.open(
          {},
          contentURI => {
            window.FilePath.resolveNativePath(
              contentURI,
              url => {
                _chooseFileOk(url, fileExt);
              },
              _chooseFileError
            );
          },
          _chooseFileError
        );
      }
    });

  const getSyncImageDirectory = () => mkdirs(documentsDirectory, 'sync-images');

  const removeSyncImageDirectory = () =>
    new Promise((success, fail) =>
      fsGetFile(`${documentsDirectory}sync-images`)
        .then(syncImagesDirectoryEntry => syncImagesDirectoryEntry.removeRecursively(success, fail))
        .catch(fail)
    );

  const getImageDirectory = () => mkdirs(documentsDirectory, 'spec-sheet-images');

  const getDatabaseDirectory = () =>
    new Promise((success, reject) => window.resolveLocalFileSystemURL(documentsDirectory, success, reject));

  const getDirectory = directory => mkdirs(applicationDirectory, directory);

  const getBackupDirectory = mkdirs(documentsDirectory, 'Backups');

  const getRestoreDirectory = () => mkdirs(documentsDirectory, 'Restore');

  const cacheRestoreFile = fileUrl =>
    new Promise((success, fail) => {
      fsGetFile(fileUrl)
        .then(zipFileEntry => {
          return getRestoreDirectory().then(restoreDirectoryEntry => {
            zipFileEntry.copyTo(
              restoreDirectoryEntry,
              uuid() + '.zip',
              cachedFile => {
                console.log('Cache end');
                success({ name: cachedFile.name, url: cachedFile.toNativeURL() });
              },
              fail
            );
          });
        })
        .catch(fail);
    });

  const getBackupCacheDirectory = cacheDirectoryName => mkdirs(documentsDirectory + 'Backups', cacheDirectoryName);

  const getBackupImgDirectory = mkdirs(documentsDirectory, 'spec-sheet-images');

  const savebase64AsImageFile = (filename, content, dataType) =>
    new Promise((success, reject) => {
      // Convert the base64 string in a Blob
      let DataBlob = b64toBlob(content, dataType);

      console.log('Writing base64 file');
      console.log('Starting to write the file :3');

      console.log('Access to the directory granted succesfully');
      getImageDirectory().then(imageDirectoryEntry => {
        imageDirectoryEntry.getFile(filename, { create: true }, file => {
          console.log('File created succesfully.');
          file.createWriter(
            fileWriter => {
              console.log('Writing content to file');
              fileWriter.onwrite = () => {
                console.log(`${filename} copied`);
                success({ name: filename, url: file.toInternalURL() });
              };
              fileWriter.onerror = () => {
                console.log(`File already exists`);
                success();
              };
              fileWriter.write(DataBlob);
            },
            error => {
              console.log('ERROR:', error);
              reject(error);
            }
          );
        });
      });
    });

  const copyEmailBck = lang => savebase64AsImageFile('specsheet-print-' + lang + '.png', specSheetImages[lang]);

  const saveHtmlFile = (filename, content) =>
    new Promise((success, reject) => {
      console.log('Starting to write the file :3');

      console.log('Access to the directory granted succesfully');
      getImageDirectory(imageDirectoryEntry => {
        imageDirectoryEntry.getFile(filename, { create: true }, file => {
          console.log('File created succesfully.');
          file.createWriter(
            fileWriter => {
              console.log('Writing content to file');
              fileWriter.onwrite = function() {
                success({ name: filename, url: file.toNativeURL() });
              };
              fileWriter.write(content);
            },
            error => {
              console.log('ERROR:', error);
              reject(error);
            }
          );
        });
      });
    });

  const saveLocalStorageInfoFile = (directoryEntry, content) =>
    new Promise((success, reject) => {
      console.log('Starting to write the file :3');

      console.log('Access to the directory granted succesfully');
      directoryEntry.getFile('userLocalInfo.json', { create: true }, file => {
        console.log('File created succesfully.');
        file.createWriter(
          function(fileWriter) {
            console.log('Writing content to file');
            fileWriter.onwrite = () => {
              success({
                name: 'userLocalInfo.json',
                url: file.toNativeURL(),
              });
            };
            fileWriter.write(content);
          },
          error => {
            console.log('ERROR:', error);
            reject(error);
          }
        );
      });
    });

  const removeImageFile = filename =>
    new Promise((success, reject) => {
      console.log('Starting to remove file :3');

      console.log('Access to the directory granted succesfully');

      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      getImageDirectory().then(imageDirectoryEntry => {
        imageDirectoryEntry.getFile(
          filename,
          {},
          file => {
            file.remove(success, _handleError);
          },
          _handleError
        );
      });
    });

  const removeFile = fileUrl =>
    new Promise((success, reject) => {
      console.log('Starting to remove file :3');

      console.log('Access to the directory granted succesfully');
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      fsGetFile(fileUrl)
        .then(file => {
          file.remove(success, _handleError);
        })
        .catch(_handleError);
    });

  const saveAndroidBackup = target =>
    new Promise((success, reject) => {
      window.resolveLocalFileSystemURL(
        applicationStorageDirectory + 'databases/' + target,
        fileEntry => {
          window.resolveLocalFileSystemURL(documentsDirectory, dirEntry => {
            fileEntry.copyTo(dirEntry, target, success, reject);
          });
        },
        reject
      );
    });

  const copyAndroidDB = () =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      window.resolveLocalFileSystemURL(
        applicationStorageDirectory + 'databases',
        dbDirEntry => {
          window.resolveLocalFileSystemURL(documentsDirectory, dirEntry => {
            dbDirEntry.copyTo(
              dirEntry,
              'db_restore',
              () => {
                console.log('copied');
                success(dirEntry);
              },
              _handleError
            );
          });
        },
        _handleError
      );
    });

  const restoreAndroidBackup = target =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      window.resolveLocalFileSystemURL(
        applicationStorageDirectory + 'databases/' + target,
        appDBEntry => {
          appDBEntry.remove(() => {
            console.log('deleted');
            window.resolveLocalFileSystemURL(
              documentsDirectory + target,
              fileEntry => {
                window.resolveLocalFileSystemURL(applicationStorageDirectory + 'databases/', dirEntry => {
                  fileEntry.copyTo(
                    dirEntry,
                    target,
                    () => {
                      console.log('copied');
                      success();
                    },
                    _handleError
                  );
                });
              },
              _handleError
            );
          }, _handleError);
        },
        _handleError
      );
    });

  const readFile = file =>
    new Promise(success => {
      var reader = new FileReader();

      reader.onloadend = () => {
        success(reader.result);
      };

      reader.readAsBinaryString(file);
    });

  const readFileBlob = file =>
    new Promise(success => {
      var reader = new FileReader();

      reader.onloadend = () => {
        const d = reader.result;
        const l = d.length;
        const array = new Uint8Array(l);
        for (let i = 0; i < l; i++) {
          array[i] = d.charCodeAt(i);
        }
        const b = new Blob([array], { type: 'application/octet-stream' });
        success(b);
      };

      reader.readAsBinaryString(file);
    });

  const saveBackupFile = (content, filename) =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      getBackupDirectory().then(backupDirectoryEntry => {
        backupDirectoryEntry.getFile(filename, { create: true }, file => {
          console.log('File created succesfully.');
          file.createWriter(fileWriter => {
            console.log('Writing content to file');
            fileWriter.onwrite = () => {
              success({ name: filename, url: file.toNativeURL() });
            };
            fileWriter.write(content);
          }, _handleError);
        });
      });
    });

  const getBackupFiles = () =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      const backupDirectoryPath = documentsDirectory + 'Backups/';

      window.resolveLocalFileSystemURL(
        backupDirectoryPath,
        backupDirectoryEntry => {
          backupDirectoryEntry.createReader().readEntries(entries =>
            success(
              entries
                .filter(entry => entry.file && entry.name.indexOf('.zip') >= 0)
                .map(entry => ({
                  localURL: entry.toNativeURL(),
                  name: entry.name,
                }))
            )
          );
        },
        _handleError
      );
    });

  const prepareBackup = databaseName =>
    new Promise((success, reject) => {
      const backupCacheDirectoryName = moment().format('YYYYMMDD-hhmmss');
      const backupCacheDirectoryPath = `${documentsDirectory}Backups/${backupCacheDirectoryName}/`;
      const backupFileName = `${documentsDirectory}Backups/${backupCacheDirectoryName}.zip`;
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      getBackupDirectory()
        .then(() => getBackupCacheDirectory(backupCacheDirectoryName))
        .then(backupCacheDirectoryEntry =>
          fsGetFile(databaseDirectory + databaseName).then(fileEntry => {
            fileEntry.copyTo(
              backupCacheDirectoryEntry,
              fileEntry.name,
              () => {
                saveLocalStorageInfoFile(backupCacheDirectoryEntry, getLocalStorageInfo())
                  .then(() => getBackupImgDirectory())
                  .then(imageDirectoryEntry => {
                    imageDirectoryEntry.copyTo(
                      backupCacheDirectoryEntry,
                      'images',
                      () => success({ backupCacheDirectoryPath, backupFileName }),
                      _handleError
                    );
                  });
              },
              _handleError
            );
          })
        );
    });

  const zipSyncImages = imagesPath =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      const syncImagesDirectoryPath = `${documentsDirectory}sync-images`;
      const syncImagesZipFile = `${documentsDirectory}sync-images.zip`;

      const copyImageToSyncFolder = (imagePath, syncImagesDirectoryEntry) =>
        new Promise((success, fail) => {
          fsGetFile(`${documentsDirectory}spec-sheet-images/${imagePath}`).then(imageFileEntry =>
            imageFileEntry.copyTo(syncImagesDirectoryEntry, imagePath, success, fail)
          );
        });

      getSyncImageDirectory().then(syncImagesDirectoryEntry => {
        const copyFiles = imagesPath.map(imagePath => copyImageToSyncFolder(imagePath, syncImagesDirectoryEntry));

        Promise.all(copyFiles).then(() => {
          Zeep.zip(
            {
              from: syncImagesDirectoryPath,
              to: syncImagesZipFile,
            },
            () => {
              console.log('Sync Images zipped');
              removeSyncImageDirectory()
                .then(() => {
                  fsGetFile(syncImagesZipFile).then(zFileEntry => {
                    zFileEntry.file(zFile => {
                      success(zFile, zFileEntry);
                    }, _handleError);
                  });
                })
                .catch(_handleError);
            },
            _handleError
          );
        });
      });
    });

  const zipSyncImage = imagePath =>
    new Promise((success, reject) => {
      console.log('returning file path');
      const imageFullPath = `${documentsDirectory}/spec-sheet-images/${imagePath}`;
      success(imageFullPath);
      // console.log('zipping:', imageFullPath);
      // console.log('file exists');
      // const _handleError = error => {
      //   console.log('ERROR:', error);
      //   reject(error);
      // };

      // const syncImagesDirectoryPath = `${documentsDirectory}sync-images`;
      // const syncImagesZipFile = `${documentsDirectory}sync-images.zip`;

      // const copyImageToSyncFolder = (imagePath, syncImagesDirectoryEntry) =>
      //   new Promise((success, fail) => {
      //     fsGetFile(`${documentsDirectory}spec-sheet-images/${imagePath}`).then(imageFileEntry =>
      //       imageFileEntry.copyTo(syncImagesDirectoryEntry, imagePath, success, fail)
      //     );
      //   });

      // getSyncImageDirectory().then(syncImagesDirectoryEntry => {
      //   const copyFiles = imagesPath.map(imagePath => copyImageToSyncFolder(imagePath, syncImagesDirectoryEntry));

      //   Promise.all(copyFiles).then(() => {
      //     Zeep.zip(
      //       {
      //         from: syncImagesDirectoryPath,
      //         to: syncImagesZipFile,
      //       },
      //       () => {
      //         console.log('Sync Images zipped');
      //         removeSyncImageDirectory()
      //           .then(() => {
      //             fsGetFile(syncImagesZipFile).then(zFileEntry => {
      //               zFileEntry.file(zFile => {
      //                 success(zFile, zFileEntry);
      //               }, _handleError);
      //             });
      //           })
      //           .catch(_handleError);
      //       },
      //       _handleError
      //     );
      //   });
      // });
    });

  const removeSyncZipFile = () => removeFile(`${documentsDirectory}sync-images.zip`);

  const zipBackup = ({ backupCacheDirectoryPath, backupFileName }) =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      Zeep.zip(
        {
          from: backupCacheDirectoryPath,
          to: backupFileName,
        },
        () => {
          console.log('backup saved');
          fsGetFile(backupCacheDirectoryPath)
            .then(backupCacheDirectoryEntry => {
              backupCacheDirectoryEntry.removeRecursively(() => {
                fsGetFile(backupFileName).then(zFileEntry => {
                  zFileEntry.file(zFile => {
                    success(zFile, zFileEntry);
                  }, _handleError);
                });
              });
            })
            .catch(_handleError);
        },
        _handleError
      );
    });

  const unzipRestore = restoreFilePath =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      const restoreFolder = restoreFilePath.replace('.zip', '');
      const dataDirectory =
        cordova.platformId === 'ios'
          ? cordova.file.documentsDirectory
          : cordova.file.applicationStorageDirectory + 'databases/';
      const rootDirectory =
        cordova.platformId == 'ios'
          ? cordova.file.documentsDirectory
          : cordova.file.externalDataDirectory || cordova.file.dataDirectory;

      Zeep.unzip(
        {
          from: restoreFilePath,
          to: restoreFolder,
        },
        () => success({ dataDirectory, rootDirectory, restoreFolder }),
        _handleError
      );
    });

  const restoreData = ({ dataDirectory, restoreFolder }) =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      fsGetFile(restoreFolder + '/userLocalInfo.json').then(localStorageInfoEntry => {
        localStorageInfoEntry.file(localStorageInfoFile => {
          const localStorageInfoReader = new FileReader();

          localStorageInfoReader.onloadend = function() {
            const localStorageInfoString = localStorageInfoReader.result;
            const localStorageInfo = JSON.parse(localStorageInfoString);
            const databaseName = localStorageInfo.databaseName;

            setLocalStorageInfo(localStorageInfoString);

            // copy database
            fsGetFile(restoreFolder + '/' + databaseName).then(databaseEntry =>
              fsGetFile(dataDirectory).then(dataDirectoryEntry => {
                const _copyDatabase = () => {
                  databaseEntry.copyTo(dataDirectoryEntry, databaseName, success, _handleError);
                };

                dataDirectoryEntry.getFile(
                  databaseName,
                  { create: false },
                  dbFileEntry => {
                    dbFileEntry.remove(() => {
                      _copyDatabase();
                    }, _handleError);
                  },
                  () => {
                    _copyDatabase();
                  }
                );
              })
            );
          };

          localStorageInfoReader.readAsText(localStorageInfoFile);
        }, _handleError);
      });
    });

  const restoreImages = ({ rootDirectory, restoreFolder }) =>
    new Promise((success, reject) => {
      const _handleError = error => {
        console.log('ERROR:', error);
        reject(error);
      };

      const imageDirectory = rootDirectory + 'spec-sheet-images';

      // copying images
      fsGetFile(restoreFolder + '/images').then(imagesEntry =>
        fsGetFile(rootDirectory).then(rootDirectoryEntry => {
          const _copyImages = () => {
            imagesEntry.copyTo(rootDirectoryEntry, 'spec-sheet-images', success, _handleError);
          };

          fsGetFile(imageDirectory).then(imagesDirectoryEntry => {
            imagesDirectoryEntry.removeRecursively(function() {
              _copyImages();
            }, _handleError);
          });
        })
      );
    });

  const saveFileToDropbox = (file, DROPBOX_APP_KEY) =>
    new Promise((success, fail) => {
      dropbox.onProgressUpload(progress => {
        console.log(
          `Uploaded ${(progress.uploaded / (1024 * 1024)).toFixed(2)} / ${(progress.size / (1024 * 1024)).toFixed(
            2
          )} Mb`
        );
      });

      dropbox.linkAccount(result => {
        // the success callback will receive a boolean indicating the user successfully completed the authorization sequence
        console.log('Linked to Dropbox? ' + result.success);
        if (result.success) {
          var content = {
            files: [file], // an array of file paths
            folder: 'Spectre Bowling Backups', // if the folder doesn't yet exist, it will be created
          };
          // drop it
          dropbox.save(
            content,
            msg => {
              console.log(`Success: ${JSON.stringify(msg)}`);
              success();
            },
            msg => {
              console.log(`Error: ${JSON.stringify(msg)}`);
              fail();
            }
          );
        }
      });
    });

  const renameDatabase = (localDatabaseName, remoteDatabaseName) =>
    localDatabaseName !== remoteDatabaseName
      ? new Promise((success, reject) => {
          console.log(`Renaming database: ${localDatabaseName} to: ${remoteDatabaseName}`);
          const _handleError = error => {
            console.log(`ERROR:`, error);
            success();
          };

          getDatabaseDirectory()
            .then(
              databaseDirectoryEntry =>
                console.log(databaseDirectoryEntry) ||
                console.log(databaseDirectory + localDatabaseName) ||
                fsGetFile(databaseDirectory + localDatabaseName).then(fileEntry => {
                  console.log(fileEntry);
                  fileEntry.copyTo(databaseDirectoryEntry, remoteDatabaseName, () => success(), _handleError);
                })
            )
            .catch(_handleError);
        })
      : Promise.resolve();

  const unzipFile = file =>
    new Promise((success, reject) => {
      getImageDirectory().then(imageDirectoryReader => {
        const imageDirectoryUrl = imageDirectoryReader.nativeURL;
        const zipFilePath = `${imageDirectoryUrl}${file}`;

        const _handleError = error => {
          console.log('ERROR:', error);
          reject(error);
        };

        Zeep.unzip(
          {
            from: zipFilePath,
            to: imageDirectoryUrl,
          },
          () => removeFile(zipFilePath).then(() => success()),
          _handleError
        );
      });
    });

  return {
    getImageUrl,
    chooseFile,
    getImageDirectory,
    getDirectory,
    getBackupDirectory,
    getRestoreDirectory,
    cacheRestoreFile,
    getBackupCacheDirectory,
    getBackupImgDirectory,
    savebase64AsImageFile,
    copyEmailBck,
    saveHtmlFile,
    saveLocalStorageInfoFile,
    removeImageFile,
    removeFile,
    readFile,
    readFileBlob,
    getBackupFiles,
    prepareBackup,
    zipBackup,
    unzipRestore,
    restoreData,
    restoreImages,
    saveFileToDropbox,
    saveAndroidBackup,
    copyAndroidDB,
    restoreAndroidBackup,
    renameDatabase,
    zipSyncImages,
    zipSyncImage,
    removeSyncZipFile,
    unzipFile,
  };
};
